import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, tap } from 'rxjs';

import { RearLoadingFrictionlessGuard } from 'src/app/rear-loading/services/rear-loading-frictionless.guard';
import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';
import { ISensor } from 'src/app/shared/interfaces/ISensor';
import { DeviceService } from 'src/app/shared/services/device/device.service';
import { HelmsdeepService } from 'src/app/shared/services/helmsdeep/helmsdeep.service';
import { SocketService } from 'src/app/shared/services/socket/socket.service';
import { RearLoadingStore } from 'src/app/shared/services/store/rear-loading/rear-loading.store';
import {
  COMMON_PATHS,
  DELIVERY_LIST_TYPE,
  DOOR_CLOSED,
  FRONT_PATHS,
  LOADED_ORDERS_EVENT,
  ORDERS_TO_DISPENSE_EVENT,
  PRESCRIPTIVE_FLOW,
  PRESENCE,
  REAR_PATHS,
  RETAIL_PATHS,
  SIDES,
  VIEW_TYPES,
} from 'src/app/shared/utils/constants';
import { Utils } from 'src/app/shared/utils/utils';
import { GANDALF_HOST } from 'src/environments/runtime';

import { Order } from '../../interfaces/IOrder';
import { OrderProcessingService } from '../../services/order-processing/order-processing.service';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
  selector: 'frodo-delivery-list',
  templateUrl: './delivery-list.component.html',
  styleUrls: ['./delivery-list.component.scss'],
})
export class DeliveryListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('code', { static: true }) searchField: ElementRef<HTMLInputElement>;
  orders = [];
  filteredOrders = [];
  paginatedOrders = [];
  isLoading = true;
  lastUpdateSelected = false;
  firstCreatedSelected = true;
  dropdownSelected = false;
  filterOptions = [
    {
      label: 'common.filterOptions.created60minAgo',
      value: '60min',
    },
    {
      label: 'common.filterOptions.created24hoursAgo',
      value: '24hours',
    },
    {
      label: 'common.filterOptions.all',
      value: 'all',
    },
  ];
  subscription: Subscription = new Subscription();
  length = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20];
  paginatorSelectConfig: MatPaginatorSelectConfig = {
    panelClass: 'delivery-list__paginator-panel',
  };
  frodoContext = '';
  homeScreenEnable: boolean;
  showKeyboard = false;
  launchScreenEnable: boolean;
  lastDropdownOption = 'all';
  positions: string[] = [];
  deviceSn: string;
  validatePresenceSensor = false;
  handedOffEnabled = false;
  handedOffModeActive = false;
  flow = PRESCRIPTIVE_FLOW;
  private focusHandler: () => void;
  private frictionlessGuardsSubscribed = false;

  constructor(
    private helmsdeepService: HelmsdeepService,
    private utils: Utils,
    private socketService: SocketService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private settingsService: SettingsService,
    private orderProcessingService: OrderProcessingService,
    private deviceService: DeviceService,
    private rearLoadingStore: RearLoadingStore,
    private rearLoadingFrictionlessGuard: RearLoadingFrictionlessGuard
  ) {
    this.socketService.setSocketHost(GANDALF_HOST);
    this.rearLoadingFrictionlessGuard.reset();
  }

  ngOnInit(): void {
    this.frodoContext = this.router.url.split('/')[1];
    this.flow = this.activatedRoute.snapshot.paramMap.get('flow') || PRESCRIPTIVE_FLOW;
    this.initSocketListeners();
    this.initSubscriptions();
  }

  ngAfterViewInit(): void {
    this.focusHandler = () => {
      this.showKeyboard = true;
    };
    this.searchField.nativeElement.addEventListener('focus', this.focusHandler);
  }

  get selectedFilterLabel(): string {
    return (
      this.filterOptions.find((option) => option.value === this.lastDropdownOption)?.label ?? ''
    );
  }

  initSubscriptions(): void {
    this.subscription.add(
      this.utils.getLoadingValue().subscribe((value) => {
        this.isLoading = value;
      })
    );
    this.subscription.add(
      this.deviceService.getSerialNum.subscribe((serialNum) => {
        this.deviceSn = serialNum;
      })
    );
    this.subscription.add(
      this.settingsService.getSettings.subscribe((response) => {
        this.homeScreenEnable = response.apps.frodo.homescreen.enable;
        this.launchScreenEnable = response.apps.frodo.launchScreen.enable;
        this.validatePresenceSensor = response.device.validatePresenceSensor;
        this.handedOffEnabled = response.apps.frodo.handedOff.isEnable;
        this.syncFrictionlessGuards();
      })
    );
    this.subscription.add(
      this.getOrders().subscribe((data) => {
        this.orders = [...data];
        this.applyCurrentFilters();
      })
    );
  }

  getOrders() {
    this.isLoading = true;
    return this.helmsdeepService
      .getData('schedules', '', 'dispenses', 'dispenseStatus=ordered,ready')
      .pipe(tap((orders: Order[]) => (this.orders = orders)));
  }

  paginateOrders(orders: Order[]): void {
    this.filteredOrders = orders;
    this.length = orders.length;
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedOrders = orders.slice(start, end);
    this.isLoading = false;
  }

  private resetAndPaginate(orders: Order[]): void {
    this.pageIndex = 0;
    this.paginateOrders(orders);
  }

  handlePageEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.paginateOrders(this.filteredOrders);
  }

  toggleHandedOffMode(): void {
    this.handedOffModeActive = !this.handedOffModeActive;
  }

  redirectToHandedOffConfirm(order: Order) {
    this.router.navigate([
      `/${this.frodoContext}/${COMMON_PATHS.HANDED_OFF_CONFIRM}`,
      {
        scheduleId: order.id,
        clientCode: order.clientCode,
        viewType: this.frodoContext,
        flow: this.flow,
      },
    ]);
  }

  loadProcess(order: any) {
    if (this.handedOffModeActive) {
      return this.redirectToHandedOffConfirm(order);
    }
    if (
      this.isRearLoadingFrictionlessModeOn() &&
      this.rearLoadingFrictionlessGuard.preventDeliveryWhenFaultySpaces()
    ) {
      return;
    }

    this.isLoading = true;
    const processOrder: IProcessOrder = {
      code: order.loadCode,
      type: DELIVERY_LIST_TYPE,
      flow: this.flow,
      side: this.frodoContext === VIEW_TYPES.REAR_LOADING ? SIDES.REAR : SIDES.FRONT,
      expireLoaded: order.expireDateTime,
      frodoContext: this.frodoContext,
      scheduleId: order.id,
      dispenseId: order.dispenses?.[0]?.id,
    };
    this.orderProcessingService.orderToProcess = processOrder;
    this.orderProcessingService.processOrder(processOrder);
  }

  redirect = (): void => {
    if (
      this.frodoContext === VIEW_TYPES.REAR_LOADING &&
      this.homeScreenEnable &&
      !this.launchScreenEnable
    )
      return;
    const pathsByContext = {
      [VIEW_TYPES.REAR_LOADING]: this.homeScreenEnable ? '/' : `/${REAR_PATHS.HOME}`,
      [VIEW_TYPES.FRONT_LOADING]: this.homeScreenEnable
        ? `/${FRONT_PATHS.WELCOME}`
        : `/${FRONT_PATHS.DELIVERY_OPTIONS}`,
      [VIEW_TYPES.RETAIL]: this.homeScreenEnable
        ? `/${RETAIL_PATHS.WELCOME}`
        : `/${RETAIL_PATHS.CORE}`,
    };
    const path = pathsByContext[this.frodoContext];
    const url = `${this.frodoContext}${path}`;
    this.utils.redirect(url);
  };

  getOrdersByInputValue(): Order[] {
    const searchValue = this.searchField?.nativeElement?.value?.toLowerCase() ?? '';
    if (!searchValue) return [...this.orders];
    return this.orders.filter((order) =>
      [order.clientCode, order.scheduleName].some((field) =>
        String(field ?? '')
          .toLowerCase()
          .includes(searchValue)
      )
    );
  }

  search(): void {
    this.showKeyboard = false;
    this.applyCurrentFilters();
    this.searchField?.nativeElement?.blur();
  }

  lastUpdate(): void {
    this.showKeyboard = false;
    this.lastUpdateSelected = true;
    this.firstCreatedSelected = false;
    this.applyCurrentFilters();
  }

  firstCreated(): void {
    this.showKeyboard = false;
    this.firstCreatedSelected = true;
    this.lastUpdateSelected = false;
    this.applyCurrentFilters();
  }

  selectChange(option: string | { value: string }): void {
    this.showKeyboard = false;
    this.dropdownSelected = true;
    const optionValue = typeof option === 'string' ? option : option.value;
    this.lastDropdownOption = optionValue;

    this.applyCurrentFilters();
  }

  applyCurrentFilters(): void {
    const orders = this.getOrdersByInputValue();
    const filteredOrders = this.getOrdersByLookbackPeriod(orders);
    const sortedOrders = this.sortOrders(filteredOrders);
    this.resetAndPaginate(sortedOrders);
  }

  private getOrdersByLookbackPeriod(orders: Order[]): Order[] {
    const now = new Date();
    switch (this.lastDropdownOption) {
      case '60min': {
        const lastHour = new Date(now.setMinutes(now.getMinutes() - 60));
        return orders.filter((order) => new Date(order.createDateTime) >= lastHour);
      }
      case '24hours': {
        const lastDay = new Date(now.setHours(now.getHours() - 24));
        return orders.filter((order) => new Date(order.createDateTime) >= lastDay);
      }
      default:
        return orders;
    }
  }

  private sortOrders(orders: Order[]): Order[] {
    if (this.lastUpdateSelected) {
      return [...orders].sort((a, b) => b.createDateTime - a.createDateTime);
    }

    if (this.firstCreatedSelected) {
      return [...orders].sort((a, b) => a.createDateTime - b.createDateTime);
    }

    return [...orders];
  }

  initSocketListeners(): void {
    this.socketService.listen(ORDERS_TO_DISPENSE_EVENT, (data) => {
      if (!this.orders.find((order) => order.id === data.msg.schedule)) {
        this.orders.push(data.msg);
        this.applyCurrentFilters();
      }
    });

    this.socketService.listen(LOADED_ORDERS_EVENT, (data) => {
      this.orders = this.orders.filter((order) => order.id !== data.msg.schedule);
      this.applyCurrentFilters();
    });

    this.socketService.listen(PRESENCE, (data) => this.handlePresence(data));
    this.socketService.listen(DOOR_CLOSED, (data) => this.handleDoorClosed(data));
  }

  private handlePresence(data): void {
    if (!this.isRearLoadingFrictionlessModeOn() || !data?.msg?.body) {
      return;
    }

    const changes = this.getSensorChanges(data);
    if (!changes) {
      return;
    }

    const presence = changes.eventType === 'FILL';
    this.positions = this.rearLoadingFrictionlessGuard.updatePositionsForPresence(
      this.getCurrentSessionPositions(),
      changes.position,
      presence
    );
    this.rearLoadingStore.updateSessionPositions(this.positions);
    this.subscribeToFrictionlessGuards();
    this.rearLoadingFrictionlessGuard.updateSessionGuards(this.positions);
  }

  private handleDoorClosed(data): void {
    if (!this.isRearLoadingFrictionlessModeOn() || !data?.msg?.body) {
      return;
    }

    const changes = this.getSensorChanges(data);
    if (changes?.eventType === 'CLOSE') {
      this.subscribeToFrictionlessGuards();
      this.rearLoadingFrictionlessGuard.updateSessionGuards(this.getCurrentSessionPositions());
    }
  }

  private getSensorChanges(data): { position: string; eventType: string } | null {
    const parsedData: ISensor = JSON.parse(data.msg.body);
    const changes = parsedData[this.deviceSn];
    return Array.isArray(changes) ? changes[0] : changes;
  }

  private syncFrictionlessGuards(): void {
    if (!this.isRearLoadingFrictionlessModeOn()) {
      return;
    }

    this.subscribeToFrictionlessGuards();
    const positions = this.getCurrentSessionPositions();
    if (positions.length) {
      this.rearLoadingFrictionlessGuard.updateSessionGuards(positions);
    }
  }

  private subscribeToFrictionlessGuards(): void {
    if (this.frictionlessGuardsSubscribed) {
      return;
    }

    this.frictionlessGuardsSubscribed = true;
    this.subscription.add(
      this.rearLoadingFrictionlessGuard.faultySpacesBlockedChanges$.subscribe()
    );
  }

  private getCurrentSessionPositions(): string[] {
    return this.positions.length
      ? [...this.positions]
      : this.rearLoadingStore.getSessionPositions();
  }

  private isRearLoadingFrictionlessModeOn(): boolean {
    return this.frodoContext === VIEW_TYPES.REAR_LOADING && this.validatePresenceSensor;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.searchField && this.focusHandler) {
      this.searchField.nativeElement.removeEventListener('focus', this.focusHandler);
      this.focusHandler = undefined;
    }
    if (this.socketService && this.socketService.socket) {
      this.socketService.socket.off(ORDERS_TO_DISPENSE_EVENT);
      this.socketService.socket.off(LOADED_ORDERS_EVENT);
      this.socketService.socket.off(PRESENCE);
      this.socketService.socket.off(DOOR_CLOSED);
    }
    this.searchField = undefined;
  }
}
